import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Coins,
  Undo2,
  RotateCcw,
  Scissors,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FloorplanState } from '@/types';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';
import { uploadAnnotatedImage } from '@/services/floorplanService';

// ============================================================================
// Types
// ============================================================================

interface PolygonPoint {
  x: number; // relative 0–1
  y: number; // relative 0–1
}

const POINT_HIT_RADIUS = 12; // px — distance to "grab" a point

// ============================================================================
// Props
// ============================================================================

interface FloorplanUnitSelectionViewProps {
  projectId: string;
  floorplanState: FloorplanState;
  onFloorplanStateUpdate: (updates: Partial<FloorplanState>) => void;
  onExtractUnit: (annotatedStoragePath: string, polygonPoints: PolygonPoint[]) => void;
  onContinue: () => void;
  onBack: () => void;
  credits: number;
  floorplanUnits: number;
}

// ============================================================================
// Component
// ============================================================================

export const FloorplanUnitSelectionView: React.FC<FloorplanUnitSelectionViewProps> = ({
  projectId,
  floorplanState,
  onFloorplanStateUpdate,
  onExtractUnit,
  onContinue,
  onBack,
  floorplanUnits,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const savedPoints = floorplanState.unitPolygonPoints;
  const [polygonPoints, setPolygonPoints] = useState<PolygonPoint[]>(savedPoints);
  const [isBurning, setIsBurning] = useState(false);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

  // Drag state
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const isDragging = useRef(false);
  const dragMoved = useRef(false);

  const previewUrl = floorplanState.previewUrl;
  const isExtracting = floorplanState.isUnitExtracting;
  const extractionError = floorplanState.unitExtractionError;

  // ========================================================================
  // Load Image
  // ========================================================================

  useEffect(() => {
    if (!previewUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setLoadedImage(img);
    img.src = previewUrl;
  }, [previewUrl]);

  // ========================================================================
  // Canvas Rendering
  // ========================================================================

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !loadedImage) return;

    const dpr = window.devicePixelRatio || 1;
    const containerWidth = container.clientWidth;
    const aspectRatio = loadedImage.naturalHeight / loadedImage.naturalWidth;
    const containerHeight = containerWidth * aspectRatio;

    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    canvas.width = containerWidth * dpr;
    canvas.height = containerHeight * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Draw image
    ctx.drawImage(loadedImage, 0, 0, containerWidth, containerHeight);

    if (polygonPoints.length === 0) return;

    // Convert relative points to pixel coords
    const pixelPoints = polygonPoints.map((p) => ({
      x: p.x * containerWidth,
      y: p.y * containerHeight,
    }));

    // Draw filled polygon (very light red)
    if (pixelPoints.length >= 3) {
      ctx.beginPath();
      ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
      for (let i = 1; i < pixelPoints.length; i++) {
        ctx.lineTo(pixelPoints[i].x, pixelPoints[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(239, 68, 68, 0.08)';
      ctx.fill();
    }

    // Draw lines
    ctx.beginPath();
    ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
    for (let i = 1; i < pixelPoints.length; i++) {
      ctx.lineTo(pixelPoints[i].x, pixelPoints[i].y);
    }
    if (pixelPoints.length >= 3) {
      ctx.closePath();
    }
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.9)';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw points (highlight dragged point)
    pixelPoints.forEach((p, i) => {
      const radius = draggingIndex === i ? 8 : 6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = draggingIndex === i ? 'rgba(239, 68, 68, 1)' : 'rgba(239, 68, 68, 0.9)';
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, [loadedImage, polygonPoints, draggingIndex]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Re-render on resize
  useEffect(() => {
    const handleResize = () => renderCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [renderCanvas]);

  // ========================================================================
  // Hit-test: find point near cursor
  // ========================================================================

  const findPointAtPosition = useCallback(
    (clientX: number, clientY: number): number | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;

      for (let i = 0; i < polygonPoints.length; i++) {
        const ptX = polygonPoints[i].x * rect.width;
        const ptY = polygonPoints[i].y * rect.height;
        const dist = Math.sqrt((px - ptX) ** 2 + (py - ptY) ** 2);
        if (dist <= POINT_HIT_RADIUS) return i;
      }
      return null;
    },
    [polygonPoints]
  );

  // ========================================================================
  // Mouse / Pointer Handlers (click to add, drag to move)
  // ========================================================================

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!loadedImage) return;
      const hitIndex = findPointAtPosition(e.clientX, e.clientY);

      if (hitIndex !== null) {
        // Start dragging existing point
        setDraggingIndex(hitIndex);
        isDragging.current = true;
        dragMoved.current = false;
        (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
      }
    },
    [loadedImage, findPointAtPosition]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDragging.current || draggingIndex === null) {
        // Update cursor based on hover
        const hitIndex = findPointAtPosition(e.clientX, e.clientY);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.cursor = hitIndex !== null ? 'grab' : 'crosshair';
        }
        return;
      }

      dragMoved.current = true;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.style.cursor = 'grabbing';

      const rect = canvas.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

      setPolygonPoints((prev) => {
        const next = [...prev];
        next[draggingIndex] = { x, y };
        return next;
      });
    },
    [draggingIndex, findPointAtPosition]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isDragging.current) {
        isDragging.current = false;
        const wasDrag = dragMoved.current;
        dragMoved.current = false;
        setDraggingIndex(null);

        // If we actually dragged, don't add a new point
        if (wasDrag) return;
      }

      if (!loadedImage) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      // Don't add if near an existing point (that was a grab attempt, not a new point)
      const hitIndex = findPointAtPosition(e.clientX, e.clientY);
      if (hitIndex !== null) return;

      setPolygonPoints((prev) => [...prev, { x, y }]);
    },
    [loadedImage, findPointAtPosition]
  );

  // ========================================================================
  // Undo / Reset
  // ========================================================================

  const handleUndo = useCallback(() => {
    setPolygonPoints((prev) => prev.slice(0, -1));
  }, []);

  const handleReset = useCallback(() => {
    setPolygonPoints([]);
  }, []);

  // ========================================================================
  // Submit Extraction
  // ========================================================================

  const handleSubmitExtraction = useCallback(async () => {
    if (polygonPoints.length < 3 || !loadedImage || !floorplanState.imageDbId) return;

    setIsBurning(true);
    try {
      const imgW = loadedImage.naturalWidth;
      const imgH = loadedImage.naturalHeight;

      // Scale polygon points to full resolution
      const fullPoints = polygonPoints.map((p) => ({
        x: p.x * imgW,
        y: p.y * imgH,
      }));

      // Expand polygon 3% outward from centroid so walls are fully included
      const centroidX = fullPoints.reduce((s, p) => s + p.x, 0) / fullPoints.length;
      const centroidY = fullPoints.reduce((s, p) => s + p.y, 0) / fullPoints.length;
      const EXPAND_FACTOR = 0.03;
      const expandedPoints = fullPoints.map((p) => ({
        x: p.x + (p.x - centroidX) * EXPAND_FACTOR,
        y: p.y + (p.y - centroidY) * EXPAND_FACTOR,
      }));

      // Calculate bounding box of expanded polygon
      const minX = Math.min(...expandedPoints.map((p) => p.x));
      const maxX = Math.max(...expandedPoints.map((p) => p.x));
      const minY = Math.min(...expandedPoints.map((p) => p.y));
      const maxY = Math.max(...expandedPoints.map((p) => p.y));
      const polyW = maxX - minX;
      const polyH = maxY - minY;

      // Add 5% padding for white margin around the unit
      const padX = polyW * 0.05;
      const padY = polyH * 0.05;
      const cropX = Math.max(0, Math.floor(minX - padX));
      const cropY = Math.max(0, Math.floor(minY - padY));
      const cropR = Math.min(imgW, Math.ceil(maxX + padX));
      const cropB = Math.min(imgH, Math.ceil(maxY + padY));
      const cropW = cropR - cropX;
      const cropH = cropB - cropY;

      // Translate expanded polygon to cropped coordinate space
      const croppedPoints = expandedPoints.map((p) => ({
        x: p.x - cropX,
        y: p.y - cropY,
      }));

      // Create cropped canvas with white-out outside polygon
      const offCanvas = document.createElement('canvas');
      offCanvas.width = cropW;
      offCanvas.height = cropH;
      const ctx = offCanvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context failed');

      // 1. Fill entire canvas white
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, cropW, cropH);

      // 2. Clip to expanded polygon and draw image — only the unit is visible
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(croppedPoints[0].x, croppedPoints[0].y);
      for (let i = 1; i < croppedPoints.length; i++) {
        ctx.lineTo(croppedPoints[i].x, croppedPoints[i].y);
      }
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(loadedImage, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      ctx.restore();

      // Upload cropped + annotated image
      const annotatedPath = await uploadAnnotatedImage(
        projectId,
        floorplanState.imageDbId,
        offCanvas
      );

      // Persist polygon points so user can adjust them if they go back
      onFloorplanStateUpdate({ unitPolygonPoints: polygonPoints });
      onExtractUnit(annotatedPath, polygonPoints);
    } catch (err) {
      console.error('[UnitSelection] Crop/upload failed:', err);
      onFloorplanStateUpdate({
        unitExtractionError: err instanceof Error ? err.message : 'Upload fehlgeschlagen',
      });
    } finally {
      setIsBurning(false);
    }
  }, [polygonPoints, loadedImage, projectId, floorplanState.imageDbId, onExtractUnit, onFloorplanStateUpdate]);

  // ========================================================================
  // Render
  // ========================================================================

  const isLoading = isExtracting || isBurning;
  const has2d = !!floorplanState.generated2dPath;
  // Pricing v4: the first generation per floorplan consumes 1 Grundriss-Einheit;
  // once a 2D exists, all further generations are free.
  const consumesUnit = !has2d;
  const hasQuota = !consumesUnit || floorplanUnits >= 1;

  // Hash-based re-extract gating: if the current polygon matches the one
  // used for the last successful extraction, skip the upload and continue.
  const currentPolygonKey = JSON.stringify(polygonPoints);
  const alreadyExtractedForCurrentPolygon =
    floorplanState.unitExtractionDone &&
    floorplanState.lastExtractedPolygonKey === currentPolygonKey &&
    polygonPoints.length >= 3;

  const canSubmit = polygonPoints.length >= 3 && !isLoading && hasQuota;
  const handlePrimaryAction = alreadyExtractedForCurrentPolygon ? onContinue : handleSubmitExtraction;
  const primaryActionDisabled = alreadyExtractedForCurrentPolygon
    ? isLoading
    : !canSubmit;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Grundriss markieren</h1>
            <p className="text-sm text-muted-foreground">
              Außenkontur des zu verarbeitenden Grundrisses festlegen
            </p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
        </Badge>
      </div>

      <FloorplanStepIndicator currentStep="unit-selection" />

      {/* Main Content: Two Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel: Floorplan Image */}
        <div className="lg:col-span-3">
          {previewUrl && (
            <Card>
              <CardContent className="p-4">
                <div className="relative">
                  <div ref={containerRef} className="relative rounded-lg overflow-hidden border bg-muted/20">
                    <canvas
                      ref={canvasRef}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      className="w-full block"
                      style={{ cursor: 'crosshair', touchAction: 'none' }}
                    />
                  </div>

                  {/* Loading Overlay */}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg z-10">
                      <div className="text-center space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                        <p className="text-sm font-medium">
                          {isBurning ? 'Bild wird vorbereitet...' : 'Grundriss wird extrahiert...'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel: Controls */}
        <div className="lg:col-span-2 space-y-4 lg:sticky lg:top-24 lg:self-start">
          {/* Instructions */}
          <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/10">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Scissors className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-red-700 dark:text-red-400">
                    Grundriss markieren
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Setzen Sie Eckpunkte um die Außenkontur des Grundrisses. Die Punkte werden automatisch verbunden. Mindestens 3 Eckpunkte sind erforderlich. Die AI generiert dadurch deutlich präzisere Ergebnisse.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Point Controls */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">
                  {polygonPoints.length} {polygonPoints.length === 1 ? 'Punkt' : 'Punkte'}
                </Badge>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleUndo}
                    disabled={polygonPoints.length === 0 || isLoading}
                    className="gap-1 h-7 px-2 text-xs"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    Rückgängig
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    disabled={polygonPoints.length === 0 || isLoading}
                    className="gap-1 h-7 px-2 text-xs"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reset
                  </Button>
                </div>
              </div>

              <Button
                onClick={handlePrimaryAction}
                disabled={primaryActionDisabled}
                className="w-full gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird extrahiert...
                  </>
                ) : alreadyExtractedForCurrentPolygon ? (
                  <>
                    Weiter zur Konfiguration
                    <ArrowRight className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <Scissors className="w-4 h-4" />
                    Grundriss extrahieren{consumesUnit ? ' (1 Grundriss)' : ' (kostenlos)'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quota Warning — only relevant for a fresh extraction */}
          {!hasQuota && !alreadyExtractedForCurrentPolygon && (
            <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/10">
              <CardContent className="p-4 flex items-start gap-3">
                <Coins className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-400 text-sm">Kein Grundriss-Kontingent verfügbar</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Buche das Grundriss-Paket dazu oder kaufe einen Einzel-Grundriss (15 €).
                    1 Grundriss beinhaltet 2D + 3D — alle Anpassungen sind danach kostenlos.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {extractionError && (
            <Card className="border-destructive bg-destructive/5">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive text-sm">Fehler bei der Extraktion</p>
                  <p className="text-xs text-muted-foreground mt-1">{extractionError}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
